import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

interface NavigationProps {
  currentSection: string;
  onNavigate: (section: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ currentSection, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'contact', label: 'Contact' }
  ];

  const serviceItems = [
    { id: 'sentry', label: 'Sentry' },
    { id: 'school-tripp', label: 'School Tripp' },
    { id: 'social-riding', label: 'Social Riding' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => onNavigate('home')}
              className="block transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-2 rounded-lg"
            >
              <img
                src="/tripp_logo.png"
                alt="Tripp"
                className="h-20 w-auto hover:opacity-90 transition-opacity duration-200"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`text-green-700 hover:text-green-800 transition-colors duration-200 font-medium ${currentSection === item.id ? 'text-green-800' : ''
                    }`}
                >
                  {item.label}
                </button>
              ))}

              {/* Services Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="flex items-center text-green-700 hover:text-green-800 transition-colors duration-200 font-medium"
                >
                  Services
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''
                    }`} />
                </button>

                {isServicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-10">
                    {serviceItems.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => {
                          onNavigate('services');
                          setIsServicesOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
                      >
                        {service.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-green-700 hover:text-green-800 transition-colors duration-200"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    onNavigate(item.id);
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-green-700 hover:text-green-800 transition-colors duration-200 font-medium"
                >
                  {item.label}
                </button>
              ))}

              <div className="px-3 py-2">
                <p className="text-green-800 font-medium mb-2">Services</p>
                {serviceItems.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => {
                      onNavigate('services');
                      setIsOpen(false);
                    }}
                    className="block w-full text-left px-4 py-1 text-green-700 hover:text-green-800 transition-colors duration-200"
                  >
                    {service.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;